import { gql } from '@apollo/client';
import { REGRESSION_EXPOSURE_FIELDS } from './factors';

/**
 * Per-holding factor contributions — the rows behind a factor bar.
 *
 * Lives here rather than beside the fund query because BOTH records carry it:
 * a scheme through `getMetaMutualFund` and a portfolio (a model's variant among
 * them) through `getPortfolio`. `factorContributions` in lib/portfolio.js reads
 * either without knowing which it was handed, so the selection has to be one
 * thing. funds.js already imports from this file, so the dependency stays
 * one-way.
 *
 * `name` is the holding's own name, so a drill row reads as a fund or a
 * stock rather than a symbol. Null for a holding the master cannot name; the
 * row then falls back to the symbol.
 */
export const HOLDING_FACTOR_CONTRIBUTIONS = `
  holdingSecuritySymbol
  name
  asOfDate
  weight
  betaContribution
  alphaScoreContribution
  rSquaredContribution
  shortTermMomentumScoreContribution
  longTermMomentumScoreContribution
  valueScoreContribution
  qualityScoreContribution
  sizeScoreContribution
  growthScoreContribution
  highVolatilityScoreContribution
  lowVolatilityScoreContribution
`;


/* ── portfolios ─────────────────────────────────────────────── */
/* The two `getPortfolio*` documents: the whole book, and one portfolio in
   full. Between them they feed the book roll-up, Client 360°, the Analyzer,
   Deep Insights and the model side of the fund deep-dive. */

/** Book-wide roll-up: every portfolio with value, returns, risk and goal linkage. */
export const GET_PORTFOLIOS = gql`
  query GetPortfolios(
    $userId: String
    $allUsers: Boolean
    $limit: Int = 200
    $skip: Int = 0
    $includeHistorical: Boolean = false
    $historicalLimit: Int = 30
    $forceRefresh: Boolean = false
  ) {
    getPortfolios(
      userId: $userId
      allUsers: $allUsers
      includeHistorical: $includeHistorical
      historicalLimit: $historicalLimit
      limit: $limit
      skip: $skip
      forceRefresh: $forceRefresh
    ) {
      success
      message
      totalCount
      portfolios {
        securitySymbol
        userId
        goalId
        portfolioName
        portfolioType
        currency
        isActive
        isModel
        latestDataDate
        totalInvestedAmount
        marketData { marketValue priceClose latestDate }
        returnMetrics {
          dailyReturn cumulativeReturn monthlyReturn ytdReturn
          oneYearReturn threeYearReturn sinceInceptionReturn
          annualizedReturn1y annualizedReturn3y totalPnl unrealizedPnl
        }
        riskMetrics {
          scenarioName scenarioValue riskType volatility sharpeRatio sortinoRatio
          maxDrawdown var95 cvar95 betaToBenchmark riskScore riskVsTarget
        }
        goalData {
          goalId goalName goalType goalStatus targetAmount
          monthlyContribution monthsToTarget riskTolerance
        }
        holdings {
          holdingSecuritySymbol name quantity weight marketValue price unrealizedPnl latestDate
        }
        historicalData { snapshotDate marketValue dailyReturn }
      }
    }
  }
`;

/**
 * Every classification axis, each bucket opening to the positions inside it.
 *
 * Shared with GET_FUND: `getPortfolio` and `getMetaMutualFund` both answer with
 * `PortfolioClassificationSummaryType`, so one selection serves a model and a
 * scheme and one component renders either.
 *
 * All seven axes are asked for even though `industries` and `regions` come back
 * null on every scheme sampled so far — a null axis costs nothing and simply
 * does not render, whereas leaving them out would hide them for good on the
 * securities that do carry them.
 *
 * `weight` is a fraction of the whole subject on both levels: a holding's 0.20
 * under EQUITY is 20% of the fund, not 20% of the equity sleeve. The buckets
 * sum to exactly 1 — unlike the look-through bucketing in getReturnAttribution,
 * which stops near 0.93 because cash and government securities fall outside
 * every security group.
 *
 * `benchmarkWeight` / `relativeWeight` are selected but currently come back null
 * at both levels; they are here so an over/underweight column lights up on its
 * own when the backend starts filling them.
 *
 * Written as a repeated inline selection rather than a named GraphQL fragment,
 * which is not a style choice: the cache runs with `addTypename: false`
 * (src/apollo.js), so nothing in a response carries `__typename` and Apollo
 * cannot decide whether `on PortfolioClassificationType` matches. It keeps the
 * rows and drops every field the fragment was to supply, which reads on screen
 * as the right number of buckets, all of them "Unclassified" at 0.00%. Every
 * other shared selection in this folder is a plain string for the same reason.
 */
const BUCKET = `
  classificationValue
  weight
  marketValue
  benchmarkWeight
  relativeWeight
  holdings {
    holdingSecuritySymbol
    name
    weight
    marketValue
    benchmarkWeight
    relativeWeight
  }
`;

export const CLASSIFICATION_SELECTION = `
  sectors { ${BUCKET} }
  industries { ${BUCKET} }
  assetClasses { ${BUCKET} }
  securityTypes { ${BUCKET} }
  countries { ${BUCKET} }
  regions { ${BUCKET} }
  currencies { ${BUCKET} }
`;

/**
 * One holding's share of a scenario's risk.
 *
 * Read from inside `riskMetrics`, never from the flat top-level
 * `holdingRiskContributions` beside it. Despite its name that field is not the
 * whole set: the resolver picks ONE scenario for it — whichever was computed
 * most recently — and answers with only those rows. So on a portfolio whose
 * last run was a stress scenario, the flat list holds that stress scenario's
 * holdings and nothing else, and a client filtering it down to the base case
 * finds nothing while the list plainly has data in it.
 *
 * Nested, each metrics row already carries its own scenario's holdings, sliced
 * server-side on `scenario_value`. That is the identity both tables are keyed
 * on; `riskType` is only the last writer's label and `scenarioName` a display
 * copy, so neither can be joined on.
 *
 * `weight` is the portfolio weight the contribution was computed against, not
 * today's. Every metric is nullable and sparsely filled: a scenario whose
 * partition has no benchmark vector returns nulls for alpha, beta and tracking
 * error while the rest computes fine.
 */
const RISK_CONTRIBUTION = `
  holdingSecuritySymbol name scenarioName scenarioValue riskType weight
  forecastExpectedReturn forecastVolatility
  forecastVar95 forecastVar99 forecastCvar95 forecastCvar99
  forecastAlpha forecastBetaToBenchmark forecastTrackingError
  forecastInformationRatio
`;

/** Everything Client 360°, the Analyzer and Deep Insights need for one portfolio. */
export const GET_PORTFOLIO = gql`
  query GetPortfolio(
    $securitySymbol: String!
    $includeHistorical: Boolean = true
    $historicalLimit: Int = 260
    $withExposures: Boolean = false
    $forceRefresh: Boolean = false
    # Per-holding factor contributions multiply every factor by the holding
    # count, so they are fetched only by the screen that opens a factor bar.
    $withFactorHoldings: Boolean = false
  ) {
    getPortfolio(securitySymbol: $securitySymbol, includeHistorical: $includeHistorical, historicalLimit: $historicalLimit, forceRefresh: $forceRefresh) {
      success
      message
      portfolio {
        securitySymbol
        userId
        portfolioName
        portfolioType
        currency
        isActive
        isModel
        latestDataDate
        createdAt
        updatedAt
        totalInvestedAmount
        goalId
        marketData { marketValue priceClose tradingVolume latestDate }
        returnMetrics {
          latestDate dailyReturn weeklyReturn monthlyReturn startDateReturn
          qtdReturn ytdReturn oneYearReturn sixMonthReturn threeYearReturn
          fiveYearReturn tenYearReturn sinceInceptionReturn cumulativeReturn
          annualizedReturn1y annualizedReturn3y annualizedReturn5y annualizedReturn10y annualizedReturnSi
          totalPnl unrealizedPnl realizedPnl totalCost
        }
        riskMetrics {
          latestDate runDate riskConfigId scenarioName scenarioValue riskType
          expectedReturn forecastBenchmarkReturn realizedBenchmarkReturn
          volatility benchmarkVolatility downsideVolatility upsideVolatility
          sharpeRatio sortinoRatio calmarRatio informationRatio
          maxDrawdown currentDrawdown maxDrawdownDuration
          var95 var99 cvar95 cvar99
          alpha betaToBenchmark trackingError correlationToBenchmark
          riskScore riskVsBenchmark riskVsTarget
          holdingRiskContributions { ${RISK_CONTRIBUTION} }
        }
        realizedRiskMetrics {
          latestDate volatility sharpeRatio sortinoRatio maxDrawdown
          var95 var99 cvar95 cvar99
          alpha betaToBenchmark trackingError riskScore
        }
        holdingRealizedRiskContributions {
          holdingSecuritySymbol name logo latestDate
          volatility benchmarkVolatility var95 var99 cvar95 cvar99
          sharpeRatio sortinoRatio maxDrawdown alpha betaToBenchmark trackingError
        }
        # Computed live by this query. The portfolio's figure is a weighted
        # average of its holdings (harmonic for P/E and P/B); each row below is
        # the holding's OWN figure beside its weight, so the rows do not sum to
        # it. All nullable, and null is "no data" rather than zero. roe,
        # dividendYield and earningsGrowth are percentages as stored; weight is
        # a fraction. The list is lib/fundamentals.js; keep the two in step.
        fundamentalMetrics {
          fundamentalLatestDate
          peRatio pbRatio roe debtToEquity dividendYield earningsGrowth
        }
        holdingFundamentalContributions {
          holdingSecuritySymbol name weight
          peRatio pbRatio roe debtToEquity dividendYield earningsGrowth
        }
        holdings {
          holdingSecuritySymbol name logo quantity price weight marketValue
          totalCost avgCost unrealizedPnl unrealizedPnlPercent realizedPnl totalPnl latestDate
          benchmarkWeight relativeWeight
        }
        holdingReturnContributions {
          holdingSecuritySymbol ytdReturnContribution oneYearReturnContribution
          mtdReturnContribution qtdReturnContribution
        }
        transactions {
          transactionId securitySymbol holdingSecuritySymbol name portfolioType
          transactionDatetime transactionType quantity price amount fees currency source
        }
        classifications {
          sectors { classificationValue weight marketValue }
          industries { classificationValue weight marketValue }
          assetClasses { classificationValue weight marketValue }
          securityTypes { classificationValue weight marketValue }
        }
        exposures: classifications @include(if: $withExposures) { ${CLASSIFICATION_SELECTION} }
        factorExposures { ${REGRESSION_EXPOSURE_FIELDS} }
        # WHICH NAMES MAKE EACH LOADING — the same rows the scheme record
        # carries, so factorContributions() in lib/portfolio.js reads either
        # without knowing which it was handed. (No backticks in here: inside a
        # gql template literal they close the template.)
        holdingFactorContributions @include(if: $withFactorHoldings) { ${HOLDING_FACTOR_CONTRIBUTIONS} }
        goalData {
          goalId goalName goalType goalStatus description createdAt
          targetAmount monthlyContribution initialContribution monthsToTarget
          riskTolerance measuringBenchmark trackingError excessReturnTarget
          plan {
            planId targetValue monthlyContribution initialContribution startingMarketValue
            monthsToTarget startDate targetInvestmentRisk status
            plannedCumulativeGains plannedCumulativeContributions plannedTotalAssets plannedFundedStatus
            progress {
              targetValue currentValue monthlyContribution fundedStatus status
              cumulativeGains cumulativeContributions totalAssets investmentReturns asOfDate
              shortfallVsPlan shortfallFlag cumulativeCashIn cumulativeCashOut
              plannedContributionsToDate contributionShortfall contributionFlag
            }
          }
        }
        historicalData {
          snapshotDate marketValue dailyReturn ytdReturn sinceInceptionReturn
          volatility sharpeRatio maxDrawdown
        }
        riskTimeSeries { latestDate portfolioVolatility benchmarkVolatility trackingError }
        benchmarkPortfolio {
          goalType riskProfile horizonYears
          allocations { assetClassId assetClassName weight }
          portfolio {
            securitySymbol portfolioName
            marketData { marketValue }
            returnMetrics { ytdReturn oneYearReturn sinceInceptionReturn }
            riskMetrics { volatility sharpeRatio maxDrawdown }
          }
        }
      }
    }
  }
`;

/**
 * The goal one portfolio holds the money for, and what that money is worth.
 *
 * Small on purpose. `getGoals` is the only query that returns a goal RECORD and
 * it answers for whoever the token is — an advisor reading a client gets
 * nothing (see `useClientGoals`) — but a portfolio carries its own goal inline,
 * and a portfolio is readable by symbol. So a client's goal names, targets and
 * contributions are reachable this way when their plan is not. One call per
 * goal portfolio, selecting nothing else.
 */
export const GET_PORTFOLIO_GOAL = gql`
  query GetPortfolioGoal($securitySymbol: String!) {
    getPortfolio(securitySymbol: $securitySymbol) {
      success
      portfolio {
        securitySymbol
        portfolioName
        goalId
        marketData { marketValue }
        totalInvestedAmount
        goalData {
          goalId
          goalName
          goalType
          goalStatus
          targetAmount
          monthlyContribution
          monthsToTarget
          riskTolerance
        }
      }
    }
  }
`;

/**
 * One portfolio as the BOOK needs it — the fields `getPortfolios` leaves empty.
 *
 * The list path is a listing and little more: verified against the running
 * backend, a client's rows come back with `totalInvestedAmount`,
 * `returnMetrics`, `holdings` and `historicalData` all null or empty, and only
 * sometimes a market value. Every book figure that is not a sum of market
 * values is built from exactly those — invested, P&L, return on cost, the
 * scheme-level drivers on the Returns tab, the risk buckets, the value series —
 * so the book reads each portfolio once through `getPortfolio`, which does
 * populate them.
 *
 * Deliberately lean: GET_PORTFOLIO selects classifications, factor exposures,
 * risk contributions, transactions and benchmarks for one portfolio on screen.
 * This is the same resolver asked for the book's own handful of fields.
 */
export const GET_PORTFOLIO_BOOK_ROW = gql`
  query GetPortfolioBookRow(
    $securitySymbol: String!
    $includeHistorical: Boolean = false
    $historicalLimit: Int = 40
  ) {
    getPortfolio(
      securitySymbol: $securitySymbol
      includeHistorical: $includeHistorical
      historicalLimit: $historicalLimit
    ) {
      success
      portfolio {
        securitySymbol
        userId
        goalId
        portfolioName
        portfolioType
        isModel
        latestDataDate
        totalInvestedAmount
        marketData { marketValue priceClose latestDate }
        returnMetrics {
          dailyReturn cumulativeReturn monthlyReturn ytdReturn
          oneYearReturn threeYearReturn sinceInceptionReturn
          annualizedReturn1y annualizedReturn3y totalPnl unrealizedPnl
        }
        riskMetrics {
          scenarioName scenarioValue riskType volatility sharpeRatio sortinoRatio
          maxDrawdown var95 cvar95 betaToBenchmark riskScore riskVsTarget
        }
        goalData {
          goalId goalName goalType goalStatus targetAmount
          monthlyContribution monthsToTarget riskTolerance
        }
        holdings {
          holdingSecuritySymbol name quantity weight marketValue price unrealizedPnl latestDate
        }
        historicalData { snapshotDate marketValue dailyReturn }
      }
    }
  }
`;

/**
 * One security's recent price, whatever kind of security it is.
 *
 * A model is weights, and a backtest needs units, so each constituent has to be
 * priced before the composition can be built. The response carries a fund's NAV
 * series and a stock's close series in separate fields and fills whichever
 * applies, so both are selected and the caller takes the last point of the one
 * that answered. A month is asked for rather than a day because the series is
 * only as fresh as the security's last published price.
 */
export const GET_LATEST_PRICE = gql`
  query GetLatestPrice($securitySymbol: String!) {
    getPriceHistory(securitySymbol: $securitySymbol, filter: "1m") {
      securitySymbol
      securityType
      dataPoints
      fundHistory { date nav dayEndNav }
      stockHistory { date close adjustedClose }
    }
  }
`;
